import { booleanAttribute, Component, inject, Input } from '@angular/core';
import { IsMobileService } from '@shared/services/is-mobile.service';

export type BadgeType = 'success' | 'warning' | 'danger' | 'info' | 'pending';
type BadgeSize = 'small' | 'medium' | 'large';

@Component({
  selector: 'sigecom-status-badge',
  template:`
    <div [ngClass]="classes(isMobile$ | async)">
      <small>{{ displayText }}</small>
    </div>
  `,
})
export class StatusBadgeComponent {
  @Input({ required: true }) type!: BadgeType;
  @Input({ required: true }) text!: string;
  @Input({ transform: booleanAttribute }) uppercase: boolean = false;
  private _size: BadgeSize = 'medium';

  @Input() set size(value: BadgeSize | null) {
    this._size = value ?? 'medium';
  }

  @Input() customClasses = '';

  isMobile$ = inject(IsMobileService).isMobile$;

  get displayText(): string {
    if(!this.uppercase) return this.text;
    return this.text.toLocaleUpperCase();
  }

  classes(isMobile: boolean | null): string {
    const base =
      'flex items-center font-semibold rounded-full border transition-colors duration-200 ' +
      this.customClasses;

    const colors: Record<BadgeType, string> = {
      success: 'text-emerald-600 border-emerald-600 bg-emerald-100',
      warning: 'text-amber-600 border-amber-600 bg-amber-100',
      danger: 'text-red-600 border-red-600 bg-red-100',
      info: 'text-blue-700 border-blue-700 bg-blue-100',
      pending: 'text-slate-600 border-slate-400 bg-slate-100',
    };

    const sizes: Record<BadgeSize, string> = {
      small: 'text-xs px-2 py-0.5',
      medium: 'text-sm px-3 py-1',
      large: 'text-base px-4 py-1.5',
    };

    const size = isMobile ? 'small' : this._size;

    return `${base} ${colors[this.type]} ${sizes[size]}`;
  }
}
